import { useState } from "react";
import { useTrackRecord } from "../hooks/useTrackRecord";
import { useTrackMeta } from "../hooks/useTrackMeta";
import { useT } from "../i18n/LanguageContext";
import { Info, TrendingUp, ArrowDownRight, PauseCircle, Activity, ChevronDown, ChevronUp } from "lucide-react";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, ReferenceLine, Area, AreaChart } from "recharts";

const fmt = (n) => `${n > 0 ? "+" : ""}${n.toFixed(2)}%`;

const START_CAPITAL = 10000;
// Returns the value of START_CAPITAL after applying the given cumulative % gain.
const calcExample = (cumulativePct) => START_CAPITAL * (1 + cumulativePct / 100);
const fmtCurrency = (n) => {
  if (n == null) return "—";
  return `€ ${Number(n).toLocaleString("de-CH", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

const VISIBLE_DEFAULT = 3;

export const TrackRecord = () => {
  const { t } = useT();
  const { rows: TRACK_RECORD } = useTrackRecord();
  const { meta } = useTrackMeta();
  const [expanded, setExpanded] = useState(false);
  const finalCum = TRACK_RECORD.length ? TRACK_RECORD[TRACK_RECORD.length - 1].cumulative : 0;
  const tradedRows = TRACK_RECORD.filter((r) => !r.paused);
  const best = tradedRows.length ? Math.max(...tradedRows.map((r) => r.monthly)) : 0;
  const worst = tradedRows.length ? Math.min(...tradedRows.map((r) => r.monthly)) : 0;
  const maxDrawdown = meta.max_drawdown ?? -1.42;

  const chartData = TRACK_RECORD.map((r) => ({
    month: r.month.replace(/(\w+) (20\d\d)/, (_, mo, yr) => `${mo.slice(0, 3)} ${yr.slice(2)}`),
    cumulative: Number(r.cumulative.toFixed(2)),
    example: Math.round(calcExample(r.cumulative)),
    paused: !!r.paused,
  }));

  return (
    <section id="track-record" data-testid="track-section" className="relative py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div className="max-w-2xl">
            <span className="al-tag">{t.track.tag}</span>
            <h2 className="mt-5 font-display font-bold text-4xl sm:text-5xl lg:text-6xl leading-tight">
              {t.track.h2a}<span className="al-grad-text">{t.track.h2b}</span>
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">{t.track.sub}</p>
          </div>
          <div className="flex gap-3 flex-wrap">
            <KpiBox icon={<TrendingUp className="h-4 w-4" />} label={t.track.kpiBest} value={fmt(best)} accent="text-emerald-600" />
            <KpiBox icon={<ArrowDownRight className="h-4 w-4" />} label={t.track.kpiWorst} value={fmt(worst)} accent="text-rose-600" />
            <KpiBox icon={<Activity className="h-4 w-4" />} label={t.track.kpiDrawdown} value={fmt(maxDrawdown)} accent="text-amber-600" />
            <KpiBox icon={<TrendingUp className="h-4 w-4" />} label={t.track.kpiCumul} value={fmt(finalCum)} accent="al-grad-text" />
          </div>
        </div>

        {/* Performance chart */}
        <div className="mt-12 al-card p-6 sm:p-8" data-testid="track-chart">
          <div className="flex items-baseline justify-between flex-wrap gap-3">
            <div>
              <div className="text-xs uppercase tracking-widest font-semibold text-slate-500">{t.track.chartTitle}</div>
              <div className="mt-1 font-display font-semibold text-lg">{t.track.chartSub}</div>
            </div>
            <div className="font-display font-bold text-2xl al-grad-text">{fmt(finalCum)}</div>
          </div>
          <div className="mt-6 h-[260px] sm:h-[320px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
                <defs>
                  <linearGradient id="alGradFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#8B5CF6" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#8B5CF6" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="alGradStroke" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#4A8FE7" />
                    <stop offset="100%" stopColor="#A855F7" />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E9E5F4" vertical={false} />
                <XAxis dataKey="month" stroke="#94A3B8" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="#94A3B8" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `${v}%`} />
                <ReferenceLine y={0} stroke="#CBD5E1" strokeDasharray="3 3" />
                <Tooltip
                  contentStyle={{ background: "white", border: "1px solid #E9E5F4", borderRadius: 12, fontSize: 13, fontFamily: "Manrope, sans-serif" }}
                  labelStyle={{ color: "#0F172A", fontWeight: 600 }}
                  formatter={(value, name, item) => {
                    if (name === "cumulative") {
                      const exampleValue = item?.payload?.example;
                      const eurLabel = exampleValue != null
                        ? `€ ${Number(exampleValue).toLocaleString("de-CH", { maximumFractionDigits: 0 })}`
                        : "";
                      return [
                        `${value > 0 ? "+" : ""}${value}%${eurLabel ? `  ·  ${eurLabel}` : ""}`,
                        t.track.kpiCumul,
                      ];
                    }
                    return [value, name];
                  }}
                />
                <Area type="monotone" dataKey="cumulative" stroke="url(#alGradStroke)" strokeWidth={2.5} fill="url(#alGradFill)" dot={{ r: 3, fill: "#8B5CF6" }} activeDot={{ r: 5 }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Subtle expandable optimization note between chart and table */}
        <details className="mt-6 group" data-testid="optimization-note">
          <summary className="list-none cursor-pointer inline-flex items-center gap-2 text-xs text-slate-500 hover:text-slate-700 transition-colors">
            <Info className="h-3.5 w-3.5" />
            <span className="uppercase tracking-widest font-semibold">{t.track.noteEyebrow}</span>
            <ChevronDown className="h-3.5 w-3.5 transition-transform group-open:rotate-180" />
          </summary>
          <div className="mt-3 rounded-xl border border-[var(--al-line)] bg-[var(--al-bg)] px-5 py-4 max-w-3xl">
            <div className="font-display font-semibold text-slate-900 text-[15px]">{t.track.noteTitle}</div>
            <p className="mt-2 text-sm text-slate-600 leading-relaxed">{t.track.noteText}</p>
          </div>
        </details>

        <div className="mt-8 al-card overflow-hidden">
          <div className="grid grid-cols-12 px-6 py-4 border-b border-[var(--al-line)] text-[11px] uppercase tracking-widest font-semibold text-slate-500">
            <div className="col-span-5 sm:col-span-3">{t.track.colMonth}</div>
            <div className="col-span-3 sm:col-span-2 text-right">{t.track.colMonthly}</div>
            <div className="col-span-4 sm:col-span-2 text-right">
              <span>{t.track.colCumul}</span>
              <span className="block sm:hidden text-[9px] normal-case tracking-normal font-medium text-slate-400 mt-0.5">€ {t.track.colExample}</span>
            </div>
            <div className="hidden sm:block sm:col-span-3 text-right">{t.track.colExample}</div>
            <div className="hidden sm:block sm:col-span-2 text-right">{t.track.colTrend}</div>
          </div>

          {(() => {
            // Always show the most recent N rows by default; expand reveals all.
            const visibleRows = expanded
              ? TRACK_RECORD
              : TRACK_RECORD.slice(-VISIBLE_DEFAULT);
            const startIndex = expanded ? 0 : Math.max(0, TRACK_RECORD.length - VISIBLE_DEFAULT);
            return visibleRows.map((r, idx) => {
              const i = startIndex + idx;
              const positive = r.monthly >= 0;
              const barWidth = Math.min(100, (Math.abs(r.cumulative) / (Math.abs(finalCum) * 1.2 || 1)) * 100);
              if (r.paused) {
                return (
                  <div key={r.month + i} data-testid={`track-row-${i}`} className="grid grid-cols-12 px-6 py-4 items-center border-b border-[var(--al-line)] last:border-b-0 bg-[var(--al-bg)]/60">
                    <div className="col-span-5 sm:col-span-3 font-display font-semibold text-slate-500">{r.month}</div>
                    <div className="col-span-7 sm:col-span-9 flex items-center gap-2 text-slate-500 text-[13px] font-medium">
                      <PauseCircle className="h-4 w-4 text-[var(--al-purple)]" /> {r.note || t.track.paused}
                    </div>
                  </div>
                );
              }
              const exampleValue = calcExample(r.cumulative);
              return (
                <div key={r.month + i} data-testid={`track-row-${i}`} className="al-row grid grid-cols-12 px-6 py-4 items-center border-b border-[var(--al-line)] last:border-b-0 transition-colors">
                  <div className="col-span-5 sm:col-span-3 font-display font-semibold">{r.month}</div>
                  <div className={`col-span-3 sm:col-span-2 text-right font-mono-tab font-medium ${positive ? "text-emerald-600" : "text-rose-600"}`}>{fmt(r.monthly)}</div>
                  <div className="col-span-4 sm:col-span-2 text-right font-mono-tab font-semibold text-slate-900">
                    {fmt(r.cumulative)}
                    <div className="sm:hidden text-[11px] font-mono-tab font-bold al-grad-text mt-0.5" data-testid={`example-mobile-${i}`}>
                      {fmtCurrency(exampleValue)}
                    </div>
                  </div>
                  <div className="hidden sm:block sm:col-span-3 text-right font-mono-tab font-bold al-grad-text" data-testid={`example-${i}`}>
                    {fmtCurrency(exampleValue)}
                  </div>
                  <div className="hidden sm:block sm:col-span-2">
                    <div className="ml-auto h-2 w-full max-w-[120px] rounded-full bg-slate-100 overflow-hidden">
                      <div className="h-full al-grad-bg" style={{ width: `${barWidth}%` }} />
                    </div>
                  </div>
                </div>
              );
            });
          })()}

          {TRACK_RECORD.length > VISIBLE_DEFAULT && (
            <button
              onClick={() => setExpanded((v) => !v)}
              data-testid="track-toggle-expand"
              aria-expanded={expanded}
              className="w-full px-6 py-4 flex items-center justify-center gap-2 text-sm font-display font-semibold text-[var(--al-purple)] hover:bg-[var(--al-grad-soft)] al-grad-soft-bg-hover transition-colors border-t border-[var(--al-line)]"
            >
              {expanded ? (
                <>
                  <ChevronUp className="h-4 w-4" />
                  {t.track.showLess}
                </>
              ) : (
                <>
                  <ChevronDown className="h-4 w-4" />
                  {t.track.showAll}
                </>
              )}
            </button>
          )}
        </div>

        <p className="mt-3 text-xs text-slate-500 max-w-3xl leading-relaxed" data-testid="example-note">
          <Info className="inline h-3.5 w-3.5 mr-1 -mt-0.5 text-slate-400" />
          {t.track.exampleNote}
        </p>

        <div className="mt-10">
          <div className="al-card p-7 al-grad-soft-bg">
            <div className="text-slate-500 text-xs uppercase tracking-widest font-semibold">{t.track.goalEyebrow}</div>
            <h3 className="mt-3 font-display font-semibold text-xl">{t.track.goalTitle}</h3>
            <p className="mt-3 text-slate-700 leading-relaxed max-w-3xl">{t.track.goalText}</p>
            <a href="#kontakt" className="mt-6 al-cta-ghost inline-flex">{t.track.goalCta}</a>
          </div>
        </div>
      </div>
    </section>
  );
};

const KpiBox = ({ icon, label, value, accent }) => (
  <div className="al-card px-5 py-4 min-w-[180px]">
    <div className="flex items-center gap-1.5 text-slate-500 text-[11px] uppercase tracking-widest font-semibold">
      {icon} {label}
    </div>
    <div className={`mt-1 font-display font-bold text-2xl ${accent}`}>{value}</div>
  </div>
);
